/** @format */
import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { useParams, useNavigate } from "react-router-dom";
import "./News.css";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { NewsCarousel } from "./NewsCarousel";

const NewsDetail = () => {
  const { slugOrId } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUsingSlug, setIsUsingSlug] = useState(true);

  const formatDate = (dateString) => {
    const options = {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Sao_Paulo",
    };
    return new Date(dateString).toLocaleDateString("pt-BR", options);
  };

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        let newsData = null;

        try {
          const slugResponse = await axios.get(
            `https://iscompatible-api.com.br/api/v1/news/slug/${slugOrId}`
          );

          if (slugResponse.data) {
            newsData = slugResponse.data;
            setIsUsingSlug(true);
            setNews(newsData);
            return;
          }
        } catch (slugError) {
          if (slugError.response?.status !== 404) {
            return "Erro ao buscar notícia";
          }
        }

        try {
          const idResponse = await axios.get(
            `https://iscompatible-api.com.br/api/v1/news/${slugOrId}`
          );

          if (idResponse.data) {
            newsData = idResponse.data;
            setIsUsingSlug(false);

            if (idResponse.data.slug) {
              navigate(`/news/${idResponse.data.slug}`, { replace: true });
              return;
            } else {
              setNews(newsData);
              return;
            }
          }
        } catch (idError) {
          return "Erro ao buscar notícia";
        }

        navigate("/not-found", { replace: true });
      } catch (error) {
        navigate("/not-found", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [slugOrId, navigate]);

  const ImageRenderer = ({ src, alt }) => {
    if (!src) return null;

    const cleanSrc = src.startsWith("http")
      ? src
      : `https://iscompatible-api.com.br/api/v1/static/${src.replace(/^\/?static\/?/, "")}`;

    return (
      <img
        src={cleanSrc}
        alt={alt}
        className="modern-carousel-image"
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    );
  };

  const YouTubeRenderer = ({ videoId }) => {
    return (
      <div className="youtube-embed-container">
        <iframe
          width="100%"
          height="400"
          src={`https://www.youtube.com/embed/${videoId}`}
          style={{ border: "none" }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="YouTube video player"
        />
      </div>
    );
  };

  if (loading) return <div className="loading">Carregando...</div>;
  if (!news) return <div className="not-found">Notícia não encontrada</div>;

  return (
    <>
      <Navbar />

      {!isUsingSlug && news.slug && (
        <div className="url-notice">
          Você está sendo redirecionado para a nova URL...
        </div>
      )}

      <div className="news-detail-container pb-4">
        <h1>{news.title}</h1>
        {news.subtitle && <h4>{news.subtitle}</h4>}
        <div className="news-meta">
          <span className="author">Por</span>
          <span className="author-name">
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => (
                  <a
                    href={href}
                    className="author-link"
                    target="_blank"
                    rel="noopener noreferrer">
                    {children}
                  </a>
                ),
                p: ({ node, children }) => <span>{children}</span>,
              }}
            >
              {news.author}
            </ReactMarkdown>
          </span>
          <span className="date">em {formatDate(news.created_at)}</span>
          {news.tags?.length > 0 && (
            <div
              className="tags"
              style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {news.tags.map((tag) => (
                <span key={tag} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        <hr />

        <div className="news-content">
          {news.thumbnail && (
            <div className="thumbnail-container-detail">
              <img
                src={`https://iscompatible-api.com.br/api/v1/static/${news.thumbnail?.replace(/^\/?static\/?/, "")}`}
                alt={news.title || "Notícia sem título"}
                className="modern-thumb-image"
              />
            </div>
          )}
          <br />
          {news.content
            ?.sort((a, b) => a.order - b.order)
            ?.map((block, index) => (
              <div key={index} className="content-block">
                {block.type === "text" ? (
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm]}
                    components={{
                      img: ImageRenderer,
                      table: ({ node, ...props }) => (
                        <div className="table-container">
                          <table {...props} />
                        </div>
                      ),
                    }}>
                    {block.value}
                  </ReactMarkdown>
                ) : block.type === "image" ? (
                  <ImageRenderer
                    src={block.value}
                    alt={`Imagem da notícia ${news.title}`}
                  />
                ) : block.type === "youtube" ? (
                  <YouTubeRenderer videoId={block.value} />
                ) : null}
              </div>
            ))}
        </div>
        <hr />
        <NewsCarousel />

        <Footer />
      </div>
    </>
  );
};

export default NewsDetail;
